import { MediaUpload } from '@wordpress/block-editor';
import Select from 'react-select';
import { DEFAULT_CSS_CLASS } from '@constants';
import {
  SelectControl,
  Button,
  ToggleControl,
  TextareaControl,
  __experimentalInputControl as InputControl,
} from '@wordpress/components';
import {
  MediaReplaceButton,
  MediaRemoveButton,
} from '@components/media-upload';

import PosterImage from './poster-image';
import PlayIcon from './play-icon';
import PauseIcon from './pause-icon';
import MobileImage from './mobile-image';

const {
  i18n: { __ },
  blockEditor: { useSetting },
} = wp;

const AspectRationSettings = ({ defaultValue, ...rest }) => {
  return (
    <>
      <span>{__('Aspect Ratios', 'b3rg')}</span>
      <SelectControl
        value={defaultValue}
        options={[
          { label: 'none', value: '' },
          { label: '21:9', value: 'ar-21by9' },
          { label: '16:9', value: 'ar-16by9' },
          { label: '4:3', value: 'ar-4by3' },
          { label: '1:1', value: 'ar-1by1' },
        ]}
        {...rest}
      />
    </>
  );
};

const VideoTypeSelect = ({ setAttributes, videoSource }) => {
  return (
    <>
      <span className="title-label">{__('Video Source', 'b3rg')}</span>
      <SelectControl
        value={videoSource}
        options={[
          { label: 'Upload (html5)', value: 'upload' },
          { label: 'URL (iframe)', value: 'url' },
          { label: 'Embed code or HTML', value: 'html' },
        ]}
        onChange={(newValue) => {
          setAttributes({ videoSource: newValue });
          if (newValue === 'url') {
            setAttributes({ aspectRatioClass: 'ar-21by9' });
          }
        }}
      />
    </>
  );
};

const UploadVideo = ({
  setAttributes,
  videoUpload,
  transparentVideo,
  videoSettings,
  enableFallbackVideo,
  videoFallback,
}) => {
  return (
    <>
      <div className={`${DEFAULT_CSS_CLASS.VIDEO}__section`}>
        <div className="btn-row">
          <MediaUpload
            onSelect={(file) => {
              setAttributes({
                videoUpload: {
                  id: file.id,
                  title: file.title,
                  filename: file.filename,
                  url: file.url,
                  fileType: file.type,
                  fileFormat: file.mime,
                },
              });
            }}
            allowedTypes={['video']}
            value={videoUpload.id}
            render={(mediaUploadProps) => {
              return (
                <div className="media-upload video-upload">
                  {videoUpload.url && (
                    <div className="upload-buttons-wrapper">
                      <MediaReplaceButton onClick={mediaUploadProps.open} />
                      <MediaRemoveButton
                        onClick={() => setAttributes({ videoUpload: {} })}
                      />
                    </div>
                  )}
                  {!videoUpload.url && (
                    <Button
                      className="media-button upload-button"
                      onClick={mediaUploadProps.open}
                    >
                      {__('Upload Video', 'berg')}
                    </Button>
                  )}
                  <div className="video-file-name">
                    <p>{videoUpload?.filename}</p>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      <div className={`${DEFAULT_CSS_CLASS.VIDEO}__section`}>
        <span className="title-label">{__('Video Settings', 'b3rg')}</span>
        <Select
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          options={[
            { value: 'autoplay', label: 'Autoplay' },
            { value: 'muted', label: 'Muted' },
            { value: 'loop', label: 'Loop' },
            { value: 'controls', label: 'Controls' },
          ]}
          value={videoSettings}
          onChange={(settings) => {
            setAttributes({
              videoSettings: settings || [],
            });
          }}
        />
      </div>
      <ToggleControl
        label={__('Enable Fallback Video', 'b3rg')}
        checked={enableFallbackVideo}
        onChange={(enableFallbackVideo) => {
          setAttributes({
            enableFallbackVideo,
          });
        }}
      />
      <span className="hint-text">
        {__(
          'Add a fallback video to be displayed in case the browser does not support the regular video. The video extension should be different from that of the regular video.',
          'b3rg',
        )}
      </span>
      {enableFallbackVideo && (
        <>
          <MediaUpload
            onSelect={(file) => {
              setAttributes({
                videoFallback: {
                  ...videoFallback,
                  id: file.id,
                  title: file.title,
                  filename: file.filename,
                  url: file.url,
                  fileType: file.type,
                  fileFormat: file.mime,
                },
              });
            }}
            type="video"
            allowedTypes={['video']}
            value={videoFallback.id}
            render={(mediaUploadProps) => {
              return (
                <div className="media-upload video-upload">
                  {videoFallback.url && (
                    <div className="upload-buttons-wrapper">
                      <MediaReplaceButton onClick={mediaUploadProps.open} />
                      <MediaRemoveButton
                        onClick={() => setAttributes({ videoFallback: {} })}
                      />
                    </div>
                  )}
                  {!videoFallback.url && (
                    <Button
                      className="media-button upload-button"
                      onClick={mediaUploadProps.open}
                    >
                      {__('Upload Fallback Video', 'b3rg')}
                    </Button>
                  )}
                  <div className="video-file-name">
                    <p>{videoFallback?.filename}</p>
                  </div>
                </div>
              );
            }}
          />
          <br />
          <ToggleControl
            label={__('Transparency', 'b3rg')}
            checked={transparentVideo}
            onChange={(newValue) => {
              setAttributes({
                transparentVideo: newValue,
              });
            }}
          />
          <span className="hint-text">
            {__(`Enable this if the fallback video is transparent`, 'b3rg')}
          </span>
        </>
      )}
    </>
  );
};

const UrlVideo = ({ setAttributes, videoExternalUrl, videoUrlValidate }) => {
  return (
    <>
      <span>{__('Video URL', 'b3rg')}</span>
      <br />
      <InputControl
        defaultValue={videoExternalUrl}
        value={videoExternalUrl}
        onChange={(videoExternalUrl) => {
          const validUrl = videoUrlValidate(videoExternalUrl);
          if (validUrl) {
            setAttributes({
              videoExternalUrl,
            });
          }
        }}
      />
      {videoExternalUrl && !videoUrlValidate(videoExternalUrl) && (
        <span className="error-text">{__('Invalid Video URL', 'b3rg')}</span>
      )}
    </>
  );
};

const HtmlVideo = ({ setAttributes, embedHtml, validHtml, blockRef }) => {
  return (
    <>
      <span>{__('Embed code or HTML', 'b3rg')}</span>
      <TextareaControl
        value={embedHtml}
        placeholder="Paste the embed code or HTML here"
        onChange={(newValue) => {
          if (blockRef.current) {
            blockRef.current.innerHTML = newValue;
          }
          setAttributes({ validHtml: true });
          if (/<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/.test(newValue)) {
            const htmlCodeEncoded = Buffer.from(newValue, 'utf-8').toString(
              'base64',
            );
            setAttributes({
              vpopupHtml: htmlCodeEncoded,
              vpopupRowHtml: newValue,
            });
          } else if (newValue.length > 0) {
            setAttributes({ validHtml: false });
          }
          setAttributes({ embedHtml: newValue });
        }}
      />
      {!validHtml && (
        <span className="error-text">
          {__('Not a valid HTML content', 'b3rg')}
        </span>
      )}
    </>
  );
};

const VideoSettings = (props) => {
  const { attributes, setAttributes, name, blockRef } = props;
  const {
    videoSource,
    transparentVideo,
    videoType,
    aspectRatioClass,
    embedHtml,
    videoSettings,
    videoPosterImage,
    mobileImage,
    pauseBtn,
    videoUpload,
    enableFallbackVideo,
    videoFallback,
    validHtml,
    playBtn,
    videoExternalUrl,
  } = attributes;

  const videoUrlValidation = useSetting(
    `custom.blocks.${name}.videoUrlValidation`,
  );

  const videoUrlValidate = (url) => {
    // If no URL validation is needed, return the URL
    if (!videoUrlValidation) return url;
    // Return false if the URL is empty or doesn't start with 'http://' or 'https://'
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  return (
    <>
      <VideoTypeSelect {...{ setAttributes, videoSource }} />

      {(videoSource === 'url' || videoSource === 'upload') && (
        <>
          {videoType !== 'thumbnail' && (
            <>
              <AspectRationSettings
                defaultValue={aspectRatioClass}
                onChange={(newValue) =>
                  setAttributes({ aspectRatioClass: newValue })
                }
              />
            </>
          )}
        </>
      )}
      {videoSource === 'upload' && (
        <UploadVideo
          {...{
            setAttributes,
            videoUpload,
            transparentVideo,
            videoSettings,
            enableFallbackVideo,
            videoFallback,
          }}
        />
      )}
      {videoSource === 'url' && (
        <UrlVideo {...{ setAttributes, videoExternalUrl, videoUrlValidate }} />
      )}
      {videoSource === 'html' && (
        <HtmlVideo {...{ setAttributes, embedHtml, validHtml, blockRef }} />
      )}
      {((videoSource === 'upload' && videoUpload.url) ||
        (videoSource === 'url' && videoUpload.url)) && (
        <>
          {(videoSource === 'upload' || videoSource === 'url') && (
            <>
              <hr />
              <PosterImage {...{ setAttributes, videoPosterImage }} />
            </>
          )}
          {videoUpload.url && (
            <div>
              <hr />
              <PlayIcon {...{ playBtn, setAttributes }} />

              <hr />

              <PauseIcon {...{ pauseBtn, setAttributes }} />
            </div>
          )}
          <hr />
          <MobileImage {...{ setAttributes, mobileImage }} />
        </>
      )}
    </>
  );
};

export default VideoSettings;
